import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './DashboardLayout.css';

const SUB_PAGE_NAMES = {
  news:         'Nyheter',
  documents:    'Dokument',
  board:        'Styrelse',
  funktionarer: 'Funktionärer',
  contact:      'Kontaktuppgifter',
  pages:        'Sidinnehåll',
  users:        'Användare',
  profile:      'Min profil',
  backup:       'Säkerhetskopiering',
  matrikel:     'Digital Matrikel',
};

const MENU_ITEMS = [
  { path: 'profile', label: 'Min profil', icon: '👤' },
  { path: 'news', label: 'Nyheter', icon: '📰' },
  { path: 'documents', label: 'Dokument', icon: '📄' },
  { path: 'board', label: 'Styrelse', icon: '👥' },
  { path: 'funktionarer', label: 'Funktionärer', icon: '🔧' },
  { path: 'contact', label: 'Kontakt', icon: '📧' },
  { path: 'matrikel', label: 'Matrikel', icon: '🏠' },
  { path: 'pages', label: 'Sidinnehåll', icon: '📝' },
  { path: 'users', label: 'Användare', icon: '⚙️', adminOnly: true },
  { path: 'backup', label: 'Säkerhet', icon: '💾', adminOnly: true },
];

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasRole } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isAdmin = hasRole('admin');

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && 
          !e.target.closest('.dash-menu-toggle')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const segments = location.pathname.split('/').filter(Boolean);
  const subSegment = segments[2];
  const isSubPage = !!subSegment && subSegment !== 'dashboard';
  const pageName = SUB_PAGE_NAMES[subSegment] || 'Dashboard';

  const filteredMenu = MENU_ITEMS.filter(item => !item.adminOnly || isAdmin);

  const isActive = (path) => location.pathname.includes(`/styrelsen/dashboard/${path}`);

  return (
    <div className="dashboard-layout">
      {/* Top Bar */}
      <header className="dash-header">
        <div className="dash-header-left">
          {/* Mobile Menu Toggle */}
          <button 
            className={`dash-menu-toggle ${menuOpen ? 'dash-menu-toggle-open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Meny"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Breadcrumb */}
          <nav className="dash-breadcrumb">
            {isSubPage ? (
              <>
                <Link to="/styrelsen/dashboard" className="dash-breadcrumb-link">
                  ← Dashboard
                </Link>
                <span className="dash-breadcrumb-separator">/</span>
                <span className="dash-breadcrumb-current">{pageName}</span>
              </>
            ) : (
              <Link to="/" className="dash-breadcrumb-link dash-breadcrumb-back">
                ← Till webbplatsen
              </Link>
            )}
          </nav>
        </div>

        {/* Desktop User Menu */}
        <div className="dash-header-right">
          <span className="dash-username">{user?.username}</span>
          <button onClick={logout} className="dash-logout-btn">
            Logga ut
          </button>
        </div>
      </header>

      <div className="dash-body">
        {/* Mobile Navigation Drawer */}
        <aside ref={menuRef} className={`dash-sidebar ${menuOpen ? 'dash-sidebar-open' : ''}`}>
          <div className="dash-sidebar-header">
            <div className="dash-sidebar-logo">Fornbo Styrelse</div>
            <div className="dash-sidebar-user">
              <span className="dash-sidebar-username">{user?.username}</span>
              <span className="dash-sidebar-role">
                {{ admin: 'Administratör', contentCreator: 'Redaktör', sekreterare: 'Sekreterare', readOnly: 'Läsbehörighet' }[user?.role] || 'Läsbehörighet'}
              </span>
            </div>
          </div>

          <nav className="dash-nav">
            <ul className="dash-nav-list">
              {filteredMenu.map(item => (
                <li key={item.path}>
                  <Link 
                    to={`/styrelsen/dashboard/${item.path}`}
                    className={`dash-nav-link ${isActive(item.path) ? 'dash-nav-link-active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="dash-nav-icon">{item.icon}</span>
                    <span className="dash-nav-label">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="dash-sidebar-footer">
            <button onClick={() => { logout(); navigate('/'); }} className="dash-sidebar-logout">
              Logga ut
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {menuOpen && <div className="dash-overlay" onClick={() => setMenuOpen(false)} />}

        {/* Main Content */}
        <main className="dash-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
